import { useEffect, useState } from "react";
import { api } from "../lib/api";

type Lead = {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  message: string | null;
  status: string;
};

const STATUS: Record<string, { label: string; color: string }> = {
  new:       { label: "Nuevo",       color: "#0891b2" },
  contacted: { label: "Contactado",  color: "#f59e0b" },
  qualified: { label: "Cualificado", color: "#10b981" },
  discarded: { label: "Descartado",  color: "#94a3b8" },
};
const STATUS_ORDER = ["new", "contacted", "qualified", "discarded"];

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("es-ES", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function SuperAdminLeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ leads: Lead[] }>("/superadmin/leads")
      .then((r) => setLeads(r.leads))
      .catch((e) => setErr(e?.message || "No se pudieron cargar los leads"))
      .finally(() => setLoading(false));
  }, []);

  async function changeStatus(id: string, status: string) {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.put(`/superadmin/leads/${id}`, { status });
    } catch {
      setLeads(prev);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("¿Borrar este lead? No se puede deshacer.")) return;
    const prev = leads;
    setLeads((ls) => ls.filter((l) => l.id !== id));
    try {
      await api.delete(`/superadmin/leads/${id}`);
    } catch {
      setLeads(prev);
    }
  }

  const newCount = leads.filter((l) => l.status === "new").length;

  const th: React.CSSProperties = {
    textAlign: "left", padding: "10px 14px", fontSize: "0.65rem", fontWeight: 600,
    textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)",
    borderBottom: "1px solid var(--clr-border2)",
  };
  const td: React.CSSProperties = {
    padding: "12px 14px", fontSize: 13.5, color: "var(--clr-text)",
    borderBottom: "1px solid var(--clr-border2)", verticalAlign: "top",
  };

  return (
    <div className="page" style={{ color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>
          Leads
        </h1>
        {newCount > 0 && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: STATUS.new.color + "1e", color: STATUS.new.color,
            borderRadius: 999, padding: "3px 11px",
            fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
          }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: STATUS.new.color, display: "inline-block", flexShrink: 0 }} />
            {newCount} nuevo{newCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      <p style={{ margin: "0 0 22px", color: "var(--clr-text3)", fontSize: "0.82rem" }}>
        Solicitudes de reunión recibidas desde el formulario de la landing.
      </p>

      {loading && <p style={{ color: "var(--clr-text3)", fontSize: "0.85rem" }}>Cargando…</p>}
      {err && <p style={{ color: "#ef4444", fontSize: "0.85rem" }}>{err}</p>}

      {!loading && !err && leads.length === 0 && (
        <div style={{
          background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
          borderRadius: 18, backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
          boxShadow: "var(--clr-shadow-sm)",
          padding: "40px 24px", textAlign: "center", color: "var(--clr-text2)",
        }}>
          Aún no hay solicitudes. Cuando alguien rellene el formulario de la landing, aparecerá aquí.
        </div>
      )}

      {!loading && !err && leads.length > 0 && (
        <div style={{
          background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
          borderRadius: 18, backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
          boxShadow: "var(--clr-shadow-sm)", overflow: "hidden",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Fecha</th>
                <th style={th}>Nombre</th>
                <th style={th}>Empresa</th>
                <th style={th}>Email</th>
                <th style={th}>Mensaje</th>
                <th style={th}>Estado</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id}>
                  <td style={{ ...td, whiteSpace: "nowrap", color: "var(--clr-text3)", fontSize: 12.5 }}>
                    {fmtDate(l.created_at)}
                  </td>
                  <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {l.first_name} {l.last_name}
                  </td>
                  <td style={td}>{l.company}</td>
                  <td style={td}>
                    <a href={`mailto:${l.email}`} style={{ color: "var(--clr-org-primary)", textDecoration: "none" }}>
                      {l.email}
                    </a>
                  </td>
                  <td style={{ ...td, maxWidth: 280, color: "var(--clr-text2)", fontSize: 13 }}>
                    {l.message || <span style={{ color: "var(--clr-text3)" }}>—</span>}
                  </td>
                  <td style={td}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                      {(() => {
                        const c = STATUS[l.status]?.color || "var(--clr-text4)";
                        return (
                          <span style={{
                            width: 6, height: 6, borderRadius: 3, flexShrink: 0,
                            background: c, display: "inline-block",
                          }} />
                        );
                      })()}
                      <select
                        value={l.status}
                        onChange={(e) => changeStatus(l.id, e.target.value)}
                        style={{
                          background: (STATUS[l.status]?.color || "#94a3b8") + "1e",
                          color: STATUS[l.status]?.color || "var(--clr-text2)",
                          border: "1px solid transparent",
                          borderRadius: 999, padding: "5px 10px", fontSize: "0.78rem", fontWeight: 600,
                          cursor: "pointer", outline: "none",
                        }}
                      >
                        {STATUS_ORDER.map((s) => (
                          <option key={s} value={s}>{STATUS[s].label}</option>
                        ))}
                      </select>
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button
                      onClick={() => remove(l.id)}
                      title="Borrar lead"
                      style={{
                        background: "transparent", border: "none", cursor: "pointer",
                        color: "var(--clr-text3)", padding: 4, display: "inline-flex", alignItems: "center",
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
